<template>
    <div class="create-key-form p-fluid pt-2">
        <div class="p-field">
            <label for="modal-key-name" class="form-label">API Key Name</label>
            <InputText
                id="modal-key-name"
                v-model="keyName"
                placeholder="Enter a name for this key"
                class="w-full"
                aria-required="true"
                autofocus
            />
            <small class="form-help-text">
                Give your key a descriptive name to identify its purpose.
            </small>
        </div>
        <div class="p-field">
            <label for="modal-key-expiration" class="form-label">
                Expiration (Optional)
            </label>
            <Calendar
                id="modal-key-expiration"
                v-model="keyExpiration"
                dateFormat="dd/mm/yy"
                placeholder="No expiration"
                :minDate="tomorrow"
                showIcon
                class="w-full"
            />
            <small class="form-help-text">
                If not set, the key will never expire.
            </small>
        </div>
    </div>
    <div class="buttons">
        <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text"
            @click="dialogRef?.close"
        />
        <Button
            label="Create API Key"
            icon="pi pi-check"
            @click="createApiKey"
            :disabled="!isFormValid || isLoading"
            :loading="isLoading"
            autofocus
        />
    </div>
</template>

<script setup lang="ts">
import Calendar from "primevue/calendar";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { Ref, computed, inject, ref } from "vue";

import { useApiKeyManager, useApiKeyStore } from "@/stores/api-key-store";

const dialogRef = inject<Ref<DynamicDialogInstance> | undefined>("dialogRef");
const apiKeyStore = useApiKeyStore();
const { isLoading } = useApiKeyManager();

// Form fields
const keyName = ref("");
const keyExpiration = ref<Date>();

// Get tomorrow's date for the minimum date in the calendar
const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);

// Form validation
const isFormValid = computed(() => {
    if (!keyName.value) return false;

    return true;
});

const createApiKey = async () => {
    if (!isFormValid.value) return;

    try {
        const result = await apiKeyStore.createApiKey(
            keyName.value,
            keyExpiration.value
        );

        if (result) {
            dialogRef?.value.close({
                success: true,
                apiKey: result,
            });
        }
    } catch (error) {
        console.error("Error creating API key:", error);
        dialogRef?.value.close({ success: false, error });
    }
};
</script>

<style scoped lang="scss">
.create-key-form {
    margin: 0;
    padding: 0.5rem 0 1rem;
}

.p-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 1.5rem;
}

.form-label {
    margin-bottom: 0.75rem;
    font-weight: 600;
    display: block;
}

.form-help-text {
    display: block;
    color: #6c757d;
    margin-top: 0.75rem;
    font-size: 0.875rem;
}

.buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.w-full {
    width: 100%;
}

.mr-2 {
    margin-right: 0.5rem;
}
</style>
